/**
 * @fileoverview Liquidity Data Loaders
 * Async wrappers for loading liquidity data series.
 * Supports both deterministic mock data and real FRED API data.
 * @module store/liquidity/loaders
 *
 * CONTRACT:
 * - All loaders are async functions returning Promise<TimeseriesPoint[]>
 * - Loaders use mock data by default; set USE_FRED_DATA = true for live data
 * - Output shape is always { t: string, v: number }[], oldest → newest
 * - Unsupported timeRange values default to "1Y" (handled by mock_series)
 */

import {
  generateMockDxySeries,
  generateMockBasisSeries,
  generateMockUsdLiquiditySeries,
} from "./mock_series.js";

import { createFredStore, getDefaultFredStore } from "../fred/index.js";

/**
 * Data source mode: "mock" or "fred"
 * @type {"mock"|"fred"}
 */
let dataMode = "mock";

/**
 * Sets the data mode for liquidity loaders.
 * @param {"mock"|"fred"} mode - Data mode to use
 */
export function setLiquidityDataMode(mode) {
  if (mode !== "mock" && mode !== "fred") {
    console.warn(`[Liquidity] Invalid mode "${mode}", using "mock"`);
    dataMode = "mock";
    return;
  }
  dataMode = mode;
  console.log(`[Liquidity] Data mode set to: ${mode}`);
}

/**
 * Gets the current data mode.
 * @returns {"mock"|"fred"} Current data mode
 */
export function getLiquidityDataMode() {
  return dataMode;
}

/**
 * Options for data loaders.
 * @typedef {Object} LoaderOptions
 * @property {string} timeRange - Time range for data (e.g., "1M", "3M", "6M", "1Y", "2Y")
 */

/**
 * A single point in a time series.
 * @typedef {Object} TimeseriesPoint
 * @property {string} t - ISO 8601 date string (e.g., "2025-01-15")
 * @property {number} v - Numeric value at this point in time
 */

/**
 * Bundle of all liquidity series.
 * @typedef {Object} LiquiditySeriesBundle
 * @property {TimeseriesPoint[]} dxy - Dollar Index series
 * @property {TimeseriesPoint[]} basis - Cross-currency basis swap series
 * @property {TimeseriesPoint[]} liquidity - Global USD liquidity series
 */

/**
 * Supported time range values.
 * @type {string[]}
 */
const SUPPORTED_TIME_RANGES = ["1M", "3M", "6M", "1Y", "2Y"];

/**
 * Default time range if an unsupported value is provided.
 * @type {string}
 */
const DEFAULT_TIME_RANGE = "1Y";

/**
 * Maps time ranges to approximate number of days for FRED queries.
 * @type {Record<string, number>}
 */
const TIME_RANGE_DAYS = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
  "2Y": 730,
};

/**
 * Normalizes time range to a supported value.
 * @param {string} timeRange - Input time range
 * @returns {string} Normalized time range (defaults to "1Y" if unsupported)
 */
function normalizeTimeRange(timeRange) {
  return SUPPORTED_TIME_RANGES.includes(timeRange)
    ? timeRange
    : DEFAULT_TIME_RANGE;
}

/**
 * Calculates start date for FRED queries based on time range.
 * @param {string} timeRange - Normalized time range
 * @returns {string} Start date in YYYY-MM-DD format
 */
function calculateStartDate(timeRange) {
  const days = TIME_RANGE_DAYS[timeRange] || TIME_RANGE_DAYS[DEFAULT_TIME_RANGE];
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  return startDate.toISOString().split("T")[0];
}

/**
 * Gets the FRED store instance.
 * @returns {ReturnType<typeof createFredStore>}
 */
function getFredStore() {
  return getDefaultFredStore();
}

/**
 * Loads DXY (Dollar Index) time series.
 *
 * @param {LoaderOptions} options - Loader options
 * @returns {Promise<TimeseriesPoint[]>} DXY series (oldest first)
 *
 * @example
 * const dxy = await loadDxySeries({ timeRange: "1Y" });
 */
export async function loadDxySeries(options) {
  const { timeRange } = options;
  const normalizedRange = normalizeTimeRange(timeRange);

  if (dataMode === "mock") {
    return Promise.resolve(generateMockDxySeries(normalizedRange));
  }

  // FRED mode: fetch real DXY data
  try {
    const store = getFredStore();
    const startDate = calculateStartDate(normalizedRange);
    
    // Use DTWEXBGS (Broad Dollar Index) as our DXY proxy
    const series = await store.getNumericSeries("DXY", {
      startDate,
    });

    if (series.length === 0) {
      console.warn("[Liquidity] No DXY data from FRED, falling back to mock");
      return generateMockDxySeries(normalizedRange);
    }

    return series;
  } catch (error) {
    console.error("[Liquidity] Error loading DXY from FRED:", error);
    console.warn("[Liquidity] Falling back to mock data");
    return generateMockDxySeries(normalizedRange);
  }
}

/**
 * Loads cross-currency basis swap time series.
 *
 * Note: FRED doesn't have direct cross-currency basis swap data.
 * In FRED mode, we approximate using credit spreads or use mock data.
 *
 * @param {LoaderOptions} options - Loader options
 * @returns {Promise<TimeseriesPoint[]>} Basis series in bps (oldest first)
 *
 * @example
 * const basis = await loadBasisSeries({ timeRange: "6M" });
 */
export async function loadBasisSeries(options) {
  const { timeRange } = options;
  const normalizedRange = normalizeTimeRange(timeRange);

  // FRED doesn't have direct basis swap data, so we always use mock
  // In a real implementation, you'd source this from Bloomberg or another provider
  // For now, even in "fred" mode, we use deterministic mock data
  if (dataMode === "mock" || dataMode === "fred") {
    // TODO: When real basis swap data source is available, implement FRED mode
    // For now, basis data is always mock (deterministic)
    return Promise.resolve(generateMockBasisSeries(normalizedRange));
  }

  return Promise.resolve(generateMockBasisSeries(normalizedRange));
}

/**
 * Loads global USD liquidity time series.
 *
 * In FRED mode, this uses Fed balance sheet data (WALCL) minus TGA minus RRP
 * as a proxy for net liquidity.
 *
 * @param {LoaderOptions} options - Loader options
 * @returns {Promise<TimeseriesPoint[]>} USD liquidity series in trillions (oldest first)
 *
 * @example
 * const liquidity = await loadUsdLiquiditySeries({ timeRange: "1Y" });
 */
export async function loadUsdLiquiditySeries(options) {
  const { timeRange } = options;
  const normalizedRange = normalizeTimeRange(timeRange);

  if (dataMode === "mock") {
    return Promise.resolve(generateMockUsdLiquiditySeries(normalizedRange));
  }

  // FRED mode: compute net liquidity from Fed data
  try {
    const store = getFredStore();
    const startDate = calculateStartDate(normalizedRange);

    // Fetch all components
    const [walcl, tga, rrp] = await Promise.all([
      store.getNumericSeries("WALCL", { startDate }),
      store.getNumericSeries("TGA", { startDate }),
      store.getNumericSeries("RRP", { startDate }),
    ]);

    if (walcl.length === 0) {
      console.warn("[Liquidity] No WALCL data from FRED, falling back to mock");
      return generateMockUsdLiquiditySeries(normalizedRange);
    }

    // Build a date-indexed map for each series
    const tgaMap = new Map(tga.map((p) => [p.t, p.v]));
    const rrpMap = new Map(rrp.map((p) => [p.t, p.v]));

    // Compute net liquidity for each WALCL date
    // Formula: Net Liquidity = WALCL - TGA - RRP (in trillions)
    const liquiditySeries = [];
    let lastTga = 0;
    let lastRrp = 0;

    for (const point of walcl) {
      // Use last known value if current date doesn't have data (different frequencies)
      const tgaValue = tgaMap.get(point.t) ?? lastTga;
      const rrpValue = rrpMap.get(point.t) ?? lastRrp;

      if (tgaMap.has(point.t)) lastTga = tgaValue;
      if (rrpMap.has(point.t)) lastRrp = rrpValue;

      // WALCL is in millions, TGA is in millions, RRP is in billions
      const walclT = point.v / 1000000; // millions → trillions
      const tgaT = tgaValue / 1000000;   // millions → trillions
      const rrpT = rrpValue / 1000;      // billions → trillions

      const netLiquidity = walclT - tgaT - rrpT;

      liquiditySeries.push({
        t: point.t,
        v: Math.round(netLiquidity * 1000) / 1000,
      });
    }

    if (liquiditySeries.length === 0) {
      console.warn("[Liquidity] Empty liquidity series, falling back to mock");
      return generateMockUsdLiquiditySeries(normalizedRange);
    }

    return liquiditySeries;
  } catch (error) {
    console.error("[Liquidity] Error computing liquidity from FRED:", error);
    console.warn("[Liquidity] Falling back to mock data");
    return generateMockUsdLiquiditySeries(normalizedRange);
  }
}

/**
 * Loads all liquidity series in parallel.
 * This is the preferred method for loading data for the Dollar Liquidity Observatory.
 *
 * @param {LoaderOptions} options - Loader options
 * @returns {Promise<LiquiditySeriesBundle>} Bundle containing dxy, basis, and liquidity series
 *
 * @example
 * const { dxy, basis, liquidity } = await loadAllLiquiditySeries({ timeRange: "1Y" });
 */
export async function loadAllLiquiditySeries(options) {
  const [dxy, basis, liquidity] = await Promise.all([
    loadDxySeries(options),
    loadBasisSeries(options),
    loadUsdLiquiditySeries(options),
  ]);

  return { dxy, basis, liquidity };
}
